import {
    ElementType, IAspectRatioHeightData, IButtonWidgetData, IBuyButtonWidgetData,
    ICalendarAppointmentScheduleWidgetData, IChatWidgetData, IDesktopHeaderSectionData, IDividerWidgetData,
    IElementData, IEmbedWidgetData, IFooterSectionData, IImageWidgetData, ILogoWidgetData, IMapWidgetData,
    IMemberMenuWidgetData, IMeshSectionData, IPagePrimaryElement, IPopUpSectionData, IPxHeightData, IScreenData,
    ISearchBarWidgetData, ISectionWithMeshData, ISpacerWidgetData, ITextWidgetData, IVideoWidgetData, IVisibility,
    IWidgetData, SectionType, WidgetType
} from "@pageBuilder/typings";
import { StringUtil } from "./string-util";

export class ElementUtil {

    public static readonly customSectionTypes: ElementType[] = [ "HeaderMobileSection", "LiteHeaderSection" ];
    public static readonly layoutSectionAreaTypes: ElementType[] = [ "HeaderSectionsArea", "FooterSectionsArea" ];
    public static readonly widgetsWithEmptyContent: ElementType[] = [
        "SpacerWidget",
        "RegisterWidget",
        "RecoveryWidget",
        "ResetPasswordWidget",
        "LoginWidget",
        "SearchResultsWidget",
        "StoreSubscriptionsWidget",
        "StoreOrderDetailsWidget",
        "PricingTableWidget",
        "CalendarEventDetailsWidget"
    ];

    public static readonly widgetsWithDefaultContent: ElementType[] = [ "CalendarEventListWidget", "CookiesButtonWidget", "CheckoutWidget", "LogoWidget" ];

    private static readonly meshSectionTypes: ElementType[] = [
        "MeshSection",
        "FooterSection",
        "HeaderDesktopSection",
        "PopUpSection"
    ];

    private static readonly sliderSectionTypes: ElementType[] = [ "MeshSection" ];

    public static readonly sectionWithoutContentBackgroundTypes: ElementType[] = [ "PopUpSection" ];

    public static isMeshSectionType(type: ElementType): boolean {
        return this.meshSectionTypes.includes(type);
    }

    public static isSliderSectionType(type: ElementType): boolean {
        return this.sliderSectionTypes.includes(type);
    }

    public static isCustomSectionType(type: ElementType): boolean {
        return this.customSectionTypes.includes(type);
    }

    public static isLayoutSectionAreaType(type: ElementType): boolean {
        return this.layoutSectionAreaTypes.includes(type);
    }

    public static isPrimaryElement(data: IElementData): boolean {
        return (data as IPagePrimaryElement).primaryElement;
    }

    public static isSectionWithoutContentBackground(element: HTMLElement): boolean {
        const elementType = ElementUtil.resolveElementType(element);
        return this.sectionWithoutContentBackgroundTypes.includes(elementType);
    }

    public static isWidgetType(element: HTMLElement): boolean {
        return element.hasAttribute("data-widget");
    }

    public static isScriptInWidgetType(element: HTMLElement): boolean {
        return element.nodeName === "SCRIPT" && (element.parentElement?.hasAttribute("data-widget") ?? false);
    }

    public static isWidgetContainer(type: ElementType): boolean {
        return type === "BoxWidget";
    }

    public static isBoxedWidget(data: IWidgetData): boolean {
        return data.placeType === "BoxWidget";
    }

    public static resolveElementType(element: HTMLElement): ElementType {
        const attributes = element.attributes;

        if (element.nodeName === "MAIN") {
            return "MainSectionsArea";
        }

        if (element.nodeName === "HEADER") {
            return "HeaderSectionsArea";
        }

        if (element.nodeName === "FOOTER") {
            return "FooterSectionsArea";
        }

        if (element.hasAttribute("data-section")) {
            const attribute = attributes.getNamedItem("data-section");
            const type = attribute?.value ? StringUtil.capitalize(attribute.value) : "";
            if (!type) { return "MeshSection"; }

            return type as SectionType;
        }

        const attribute = attributes.getNamedItem("data-widget");
        const type = attribute ? StringUtil.capitalize(attribute.value) : "";
        return type as WidgetType;
    }

    public static resolveWidgetHeightData(data?: IWidgetData): IPxHeightData | IAspectRatioHeightData | null | undefined {
        if (!data) {
            return;
        }

        switch (data.type) {
            case "ButtonWidget":
                return (data as IButtonWidgetData).style?.heightData;
            case "BuyButtonWidget":
                return (data as IBuyButtonWidgetData).style?.heightData;
            case "ChatWidget":
                return (data as IChatWidgetData).style?.heightData;
            case "DividerWidget":
                return (data as IDividerWidgetData).style?.heightData;
            case "ImageWidget":
                return (data as IImageWidgetData).style?.heightData;
            case "EmbedWidget":
                return (data as IEmbedWidgetData).style?.heightData;
            case "MapWidget":
                return (data as IMapWidgetData).style?.heightData;
            case "SpacerWidget":
                return (data as ISpacerWidgetData).style?.heightData;
            case "VideoWidget":
                return (data as IVideoWidgetData).style?.heightData;
            case "SearchBarWidget":
                return (data as ISearchBarWidgetData).style?.heightData;
            case "TextWidget":
                return (data as ITextWidgetData).style?.heightData;
            case "MemberMenuWidget":
                return (data as IMemberMenuWidgetData).style?.heightData;
            case "LogoWidget":
                return (data as ILogoWidgetData).style?.heightData;
            case "CalendarAppointmentScheduleWidget":
                return (data as ICalendarAppointmentScheduleWidgetData).style?.heightData;
            default:
                return;
        }
    }

    public static setVisibilityClasses(element: HTMLElement, visibility: IVisibility): void {
        element.classList.remove("hide-in-desktop");
        element.classList.remove("hide-in-tablet");
        element.classList.remove("hide-in-mobile");

        if (!visibility.desktop){
            element.classList.add("hide-in-desktop");
        }
        if (!visibility.tablet){
            element.classList.add("hide-in-tablet");
        }
        if (!visibility.mobile){
            element.classList.add("hide-in-mobile");
        }
    }

    public static getAreaTypeBySectionType(sectionType: SectionType): "FooterSectionsArea" | "HeaderSectionsArea" | "MainSectionsArea" {
        switch (sectionType) {
            case "FooterSection":
                return "FooterSectionsArea";
            case "HeaderDesktopSection":
            case "HeaderMobileSection":
                return "HeaderSectionsArea";
            default:
                return "MainSectionsArea";
        }
    }

    public static getScreensData(sectionData: ISectionWithMeshData): IScreenData[] {
        if (sectionData.type === "FooterSection") {
            return [sectionData as IFooterSectionData];
        }

        if (sectionData.type === "HeaderDesktopSection") {
            return [sectionData as IDesktopHeaderSectionData];
        }

        if (sectionData.type === "PopUpSection") {
            return [sectionData as IPopUpSectionData];
        }

        if (sectionData.type === "MeshSection") {
            return (sectionData as IMeshSectionData).slides;
        }

        return [];
    }

}
